import React, { useMemo, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import useSWR from "swr";
import { useTheme } from "../config/ThemeContext";
import apiClient from "../services/apiClient";
const fetcher = (url) => apiClient.get(url).then(res => res.data);
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";

function Home() {
  const colors = useTheme();
  const navigate = useNavigate();
	const { data, error, isLoading } = useSWR(
	  "/api/matches/dashboard",
	  fetcher,
	  { revalidateOnFocus: false }
	);

  const { activeSeason, ongoingMatches, upcomingMatches, finishedMatches } = data || {};
  const activeSeasonId = activeSeason?.id;
  const isFetching = isLoading && !data;

  const renderMatchRow = useCallback(
    (match, isFinished, isOngoing = false) => {
      const date = new Date(match.startTime.replace(/Z$/, "")); //Remove Z so that it displays as stored in db. in future need to store dates in db as UTC instead of local time. 
      const formattedDate = date.toLocaleDateString();
      const formattedTime = date.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      });
      return (
        <div
          key={match.id}
          className="flex justify-between items-center py-2 border-b last:border-b-0"
        >
          <div>
            <div className={`font-semibold ${colors.neutral.text}`}>{match.matchTitle}</div>
            <div className="text-sm text-gray-500">
              {formattedDate} {formattedTime}
            </div>
          </div>
          <div className="flex space-x-2">
            {isFinished ? (
              <>
                <button
                  title="P2 Match Report"
                  onClick={() => window.open(`/matches/${match.id}/report`, "_blank")}
                  className={colors.buttons.secondary}
                >
                  📋 P2
                </button>
                <button
                  title="P3 Skill Ranking"
                  onClick={() => window.open(`/matches/${match.id}/players-ranking`, "_blank")}
                  className={colors.buttons.secondary}
                >
                  ⭐ P3
                </button>
              </>
            ) : (
              <button
                onClick={() => navigate(`/matches/pre-match/${match.id}`)}
                className={colors.buttons.primary}
              >
                {isOngoing ? "Continue to Live Scoring" : "🏐 Start Match"}
              </button>
            )}
          </div>
        </div>
      );
    },
    [colors.buttons.primary, colors.buttons.secondary, colors.neutral.text, navigate]
  );

  const ongoingRows = useMemo(
    () => (ongoingMatches || []).map((m) => renderMatchRow(m, false, true)),
    [ongoingMatches, renderMatchRow]
  );
  const upcomingRows = useMemo(
    () => (upcomingMatches || []).map((m) => renderMatchRow(m, false, false)),
    [upcomingMatches, renderMatchRow]
  );
  const finishedRows = useMemo(
    () => (finishedMatches || []).map((m) => renderMatchRow(m, true)),
    [finishedMatches, renderMatchRow]
  );

  if (isFetching) {
    return (
      <div className="p-4">
        <div className="h-6 bg-gray-200 rounded w-1/3 mb-4 animate-pulse" />
        <div className="space-y-2">
          {[...Array(5)].map((_, i) => (
            <div
              key={i}
              className="h-8 bg-gray-200 rounded animate-pulse"
            />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 text-red-500">
        Failed to load dashboard data.
      </div>
    );
  }

  // Fresh account: authenticated but no active season set up yet
  if (!isFetching && data && !activeSeason) {
    return (
      <div className="flex h-screen bg-gray-100 items-center justify-center">
        <div className="bg-white shadow-md rounded-lg p-8 max-w-md text-center">
          <div className="text-4xl mb-4">🏐</div>
          <h2 className="text-xl font-semibold mb-2">No Active Season</h2>
          <p className="text-gray-500 mb-4">
            You don't have an active season configured yet. Go to <strong>Seasons</strong> to create or activate one.
          </p>
          <Link
            to="/season-management"
            className="inline-block px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 font-medium"
          >
            📅 Go to Seasons
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-100">
      {/* Sidebar */}
      <div className="w-64 bg-white shadow-lg p-4">
        <img
          src="/assets/logos/volleyfly-logo-300x111.png"
          alt="VolleyFly"
          className="mb-6 mx-auto"
        />
        <nav>
          <ul className="space-y-2">
            <li>
              <Link
                to={
                  activeSeasonId
                    ? `/matches/setup/${activeSeasonId}`
                    : "#"
                }
                className={`block p-3 rounded font-medium ${
                  activeSeasonId
                    ? "hover:bg-gray-100"
                    : "opacity-50 cursor-not-allowed"
                }`}
              >
                🏐 Live Scoring Matches
              </Link>
            </li>
            <li>
				<Link
				  to={
					activeSeasonId
					  ? `/seasons/${activeSeasonId}/team-ranking` // 👈 Points to your new React Page
					  : "#"
				  }
				  // Remove target="_blank" so it opens in the app (or keep it if you really want a new tab)
				  className={`block p-3 rounded font-medium ${
					activeSeasonId
					  ? "hover:bg-gray-100" // Added text color for better visibility
					  : "opacity-50 cursor-not-allowed"
				  }`}
				  // Optional: prevent navigation if disabled
				  onClick={(e) => !activeSeasonId && e.preventDefault()}
				>
				  🥇 Team Ranking (P4)
				</Link>
            </li>
            <li>
			<Link
			  to={
				activeSeasonId
				  ? `/seasons/${activeSeasonId}/players-ranking` // 👈 Points to your new PlayersRanking.js page
				  : "#"
			  }
			  // We remove target="_blank" and rel for internal navigation
			  className={`block p-3 rounded font-medium ${
				activeSeasonId
				  ? "hover:bg-gray-100"
				  : "opacity-50 cursor-not-allowed"
			  }`}
			  // Prevent default click if no season is active
			  onClick={(e) => !activeSeasonId && e.preventDefault()}
			>
			  🏅 Players Ranking (P5)
			</Link>
            </li>
            <li>
              <Link
                to={
                  activeSeasonId
                    ? `/team-management/${activeSeasonId}`
                    : "#"
                }
                className={`block p-3 rounded font-medium ${
                  activeSeasonId
                    ? "hover:bg-gray-100"
                    : "opacity-50 cursor-not-allowed"
                }`}
              >
                🧑‍🤝‍🧑 Teams and Players
              </Link>
            </li>
            <li>
			<Link
			  to={
				activeSeasonId
				  ? `/schedule/${activeSeasonId}` // 👈 Use the secure page route
				  : "#"
			  }
			  className={`block p-3 rounded font-medium ${
				activeSeasonId
				  ? "hover:bg-gray-100" // Styled when active
				  : "opacity-50 cursor-not-allowed" // Styled when inactive
			  }`}
			  onClick={(e) => !activeSeasonId && e.preventDefault()} // Prevent navigation if no season is active
			>
			  🕒 Schedule
			</Link>
            </li>
            <li>
              <Link
                to="/season-management"
                className="block p-3 rounded hover:bg-gray-100 font-medium"
              >
                📅 Seasons
              </Link>
            </li>
          </ul>
        </nav>
      </div>
      <div className="flex-1 p-6 overflow-y-auto">
        {ongoingRows.length > 0 && (
          <div className="bg-white shadow-md rounded-lg p-4 mb-6 border-t-4 border-yellow-300">
            <h2 className={`text-xl font-semibold mb-4 ${colors.neutral.text}`}>Ongoing Matches</h2>
            {ongoingRows}
          </div>
        )}
        <div className="bg-white shadow-md rounded-lg p-4 mb-6 border-t-4 border-blue-300">
          <h2 className={`text-xl font-semibold mb-4 ${colors.neutral.text}`}>Upcoming Matches</h2>
          {upcomingRows.length > 0 ? upcomingRows : (
            <div className="text-gray-500">No upcoming matches.</div>
          )}
        </div>
        <div className="bg-white shadow-md rounded-lg p-4 border-t-4 border-green-300">
          <h2 className={`text-xl font-semibold mb-4 ${colors.neutral.text}`}>Recently Finished Games</h2>
          {finishedRows.length > 0 ? finishedRows : (
            <div className="text-gray-500">No finished games yet.</div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Home;